'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Globe, ShieldCheck, Radar, Crosshair, Send, Users, Eye, Clock, Activity, TrendingUp, AlertCircle, MessageSquare } from 'lucide-react'
import { baseDashboardClasses } from '../BaseDashboard'
import { PageFrame } from '../ui'
import { apiGet } from './entriesApi'
import type { ProcessedTheme } from '../ProcessedEntry/DomainEntry/processedTypes'

// Brand Integrity: one brand's standing at a glance -- a status orb, the two
// signals Trust Fence detects (pages impersonating the brand, fraud messages
// reaching consumers), a short narrative, and the third-party feed's numbers
// below ours, the way a brand owner wants to read it. Modelled on the
// brand-threat-beacon mock (Lovable) and on the CTT status deck of October
// 2026: the same page for every brand, pre-scoped to its platform like
// Unified Entries, so a tenant only ever sees its own standing.
//
// Everything shown is derived server-side (backend queries/brandIntegrity.ts)
// from the brand's unified entries, the auto-domain candidates, their probes
// and the actions filed for it; the status itself is the backend's rule,
// repeated in the (i) popover. The segmented control shows the computed
// status; an Editor may click another segment to preview how the page reads
// in that state (same numbers, that state's copy) -- the mock's demo toggle,
// kept for showing the product. Nothing on this page writes.

export type IntegrityStatus = 'secure' | 'elevated' | 'under_threat'

type WeekRow = { week_start: string; pages: number; communications: number; feed: number }

type Summary = {
  platform_id: number
  computed_at: string
  status: IntegrityStatus
  rule: { underThreatLiveTargets: number; underThreatLiveAgeHours: number; elevatedRecentDays: number; staleProbeDays: number; windowWeeks?: number }
  live: { count: number; oldest_hours: number | null; targets: Array<{ host: string; url: string; detected_at: string | null; last_checked_at: string | null; reports: number; origin?: string; feed?: boolean; content_verdict?: string | null }> }
  // d7: added 2026-10-21; absent from an older backend.
  targets: { total: number; known: number; stale: number; unknown: number; d7?: number; d30: number; d90: number }
  contained: { total: number; d7?: number; d30: number; d90: number; rate_90d: number | null; rate_total: number | null }
  detections: { h72: number; d7: number; d30: number; d90: number; total: number }
  removal: { median_minutes_90d: number | null; median_minutes_total: number | null; timed_90d: number; timed_total: number }
  actions: { filed_total: number; filed_30d: number; filed_quarter: number; filed_window?: number; acknowledged_total: number; resolved_total: number; pending: number }
  channels: { sources: string[]; count: number }
  watch: { last_probe_at: string | null; last_pipeline_at: string | null; last_entry_at: string | null; entries: number }
  // The two signals, the feed and the weekly series: added 2026-10-21; an
  // older backend has none of them and the page falls back to the tiles.
  window?: { since: string; until: string; weeks: number }
  pages?: { detected: number; detected_in_window: number; live: number; live_in_window: number; contained: number; stale: number; unknown: number; oldest_live_days: number | null; in_feed: number; in_feed_in_window: number; from_candidates: number }
  communications?: { total: number; in_window: number; by_channel: { sms: number; email: number; whatsapp: number; other: number }; unrelated_to_pages: number; first_in_window: string | null }
  feed?: { sources: Record<string, string>; domains: number; domains_in_window: number; pre_flagged: number; pre_flagged_in_window: number; live: number; latest_at: string | null; first_in_window: string | null }
  by_week?: WeekRow[]
}

// Survives brand switches; a refresh clears it.
const _cache = new Map<number, { summary: Summary; at: number }>()
const CACHE_TTL = 5 * 60 * 1000

const STATUS_ORDER: IntegrityStatus[] = ['secure', 'elevated', 'under_threat']
const STATUS_LABEL: Record<IntegrityStatus, string> = { secure: 'Secure', elevated: 'Elevated', under_threat: 'Under threat' }
const STATUS_HEADLINE: Record<IntegrityStatus, string> = {
  secure: 'Protected, and staying that way.',
  elevated: 'Spotted early. Handled fast.',
  under_threat: 'Attacked — and fighting back.',
}
// The orb's colour per status: the theme's own success / warning / destructive
// tokens (globals.css), so the orb follows light and dark like everything else.
const STATUS_COLOR: Record<IntegrityStatus, string> = {
  secure: 'var(--success)',
  elevated: 'var(--warning)',
  under_threat: 'var(--destructive)',
}
const STATUS_TEXT: Record<IntegrityStatus, { light: string; dark: string }> = {
  secure: { light: 'text-success', dark: 'text-success' },
  elevated: { light: 'text-warning', dark: 'text-warning' },
  under_threat: { light: 'text-destructive', dark: 'text-destructive' },
}

// ─── Formatting ───────────────────────────────────────────────────────────────

const HOUR_MIN = 60
const DAY_MIN = 24 * HOUR_MIN

function formatMinutes(minutes: number | null): string {
  if (minutes == null || !Number.isFinite(minutes)) return '—'
  const m = Math.max(0, minutes)
  if (m < HOUR_MIN) return `${Math.round(m)} min`
  if (m < 2 * DAY_MIN) return `${Math.round(m / HOUR_MIN)}h`
  return `${(m / DAY_MIN).toFixed(1)} d`
}

function formatHours(hours: number | null): string {
  if (hours == null || !Number.isFinite(hours)) return '—'
  if (hours < 1) return 'under an hour'
  if (hours < 48) return `${Math.round(hours)} hours`
  return `${Math.round(hours / 24)} days`
}

function relativeTime(iso: string | null): string {
  if (!iso) return 'never'
  const ms = Date.parse(iso)
  if (!Number.isFinite(ms)) return 'unknown'
  const diff = Date.now() - ms
  const m = Math.floor(diff / 60000)
  if (m < 1) return 'just now'
  if (m < 60) return `${m} min ago`
  const h = Math.floor(m / 60)
  if (h < 48) return `${h}h ago`
  return `${Math.floor(h / 24)} d ago`
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
// "1 Sep", in UTC, the way the backend cuts its weeks.
function shortDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (!Number.isFinite(d.getTime())) return '—'
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`
}

function percent(rate: number | null): string {
  return rate == null ? '—' : `${Math.round(rate * 100)}%`
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`
const rate = (n: number, weeks: number) => (weeks > 0 ? Math.round((n / weeks) * 10) / 10 : 0)

// The last 7 days' pages when none is live. Not live is not down: a page
// never probed, or not re-checked lately, is neither, so "every page already
// down" is said only when every one of them was seen down (2026-10-21: it was
// printed beside "0 of 11 pages ... are down").
function recentPagesDown(s: Summary): string {
  const pages = s.targets.d7, down = s.contained.d7
  if (pages == null || down == null) return ''
  if (pages === 0) return ', none of them linking a page'
  if (down === pages) return pages === 1 ? ', the page it linked already down' : `, all ${pages} pages they linked already down`
  return `, ${down} of ${plural(pages, 'page')} they linked confirmed down and the rest not yet checked`
}

// Pages last seen up but not re-checked within the rule's window: never
// claimed as live, never hidden either.
const unverified = (s: Summary) =>
  s.targets.stale > 0 ? ` ${plural(s.targets.stale, 'older page')} last seen up ${s.targets.stale === 1 ? 'has' : 'have'} not been re-checked in ${s.rule.staleProbeDays}+ days.` : ''

// ─── Legacy copy per status (older backend without the two signals) ──────────

type Tile = { label: string; value: string }
type Item = { icon: React.ComponentType<{ className?: string }>; title: string; body: string }

function composeLegacyCopy(status: IntegrityStatus, s: Summary, brand: string): { tiles: Tile[]; items: Item[] } {
  const live = s.live.count
  const lastCheck = relativeTime(s.watch.last_probe_at)
  const sourcesList = s.channels.sources.slice(0, 4).join(', ') + (s.channels.count > 4 ? ` and ${s.channels.count - 4} more` : '')
  const median90 = formatMinutes(s.removal.median_minutes_90d)

  if (status === 'secure') {
    return {
      tiles: [
        { label: 'Active threats', value: String(live) },
        { label: 'Pages contained', value: s.targets.known > 0 ? `${s.contained.total}/${s.targets.known}` : '—' },
        { label: 'Last check', value: s.watch.last_probe_at ? relativeTime(s.watch.last_probe_at) : '—' },
      ],
      items: [
        { icon: Globe, title: 'Nothing gets through', body:
          (s.detections.h72 === 0
            ? 'No scam report naming the brand in the last 72 hours. '
            : `${plural(s.detections.h72, 'scam report')} in the last 72 hours, none with a page still up. `)
          + (s.targets.known > 0
            ? `Every one of the ${plural(s.targets.known, 'page')} checked to date is down.`
            : 'No fraudulent page has been reported so far.')
          + unverified(s) },
        { icon: ShieldCheck, title: 'Your channels, verified', body:
          s.channels.count > 0
            ? `${plural(s.channels.count, 'source')} feed this brand's watch (${sourcesList}). ${plural(s.detections.total, 'report')} screened in all, ${s.watch.entries} entries reviewed.`
            : 'No source has reported anything for this brand yet; the watch is in place.' },
        { icon: Radar, title: 'Watch never pauses', body:
          `Reported pages stay on the monitor's rolling re-probe; the last check ran ${lastCheck}. The moment anything comes back up, your team knows first.` },
      ],
    }
  }

  if (status === 'elevated') {
    return {
      tiles: [
        { label: 'Lookalikes contained', value: String(s.contained.d30) },
        { label: 'Median response', value: median90 },
        { label: 'Takedowns this quarter', value: String(s.actions.filed_quarter) },
      ],
      items: [
        { icon: Crosshair, title: 'Caught early', body:
          `${plural(s.detections.d7, 'scam report')} in the last 7 days`
          + (live > 0 ? `, ${plural(live, 'page')} still up and under watch` : recentPagesDown(s))
          + (s.live.oldest_hours != null && live > 0 ? ` — the oldest for ${formatHours(s.live.oldest_hours)}.` : '.') },
        { icon: Send, title: 'Contained at the source', body:
          `${s.contained.d30} of ${plural(s.targets.d30, 'page')} reported in the last 30 days are down. `
          + `${plural(s.actions.filed_30d, 'takedown request')} went out in the same period${s.actions.pending > 0 ? `, ${s.actions.pending} more planned` : ''}.` },
        { icon: Activity, title: 'Your record holds', body:
          (s.removal.timed_90d > 0
            ? `Median detection-to-removal of ${median90} over the last 90 days (${plural(s.removal.timed_90d, 'page')} timed). `
            : 'No page needed a timed removal in the last 90 days. ')
          + (live > 0 ? 'Review the live targets below and confirm any authorised partner.' : `The last check ran ${lastCheck}.`)
          + unverified(s) },
      ],
    }
  }

  return {
    tiles: [
      { label: 'Live threats', value: String(live) },
      { label: 'Median time to removal', value: median90 },
      { label: 'Enforcement success', value: percent(s.contained.rate_90d) },
    ],
    items: [
      { icon: Crosshair, title: 'The campaign is mapped', body:
        `${plural(live, 'page')} impersonating ${brand} ${live === 1 ? 'is' : 'are'} up right now`
        + (s.live.oldest_hours != null ? `, the oldest for ${formatHours(s.live.oldest_hours)}` : '')
        + `. ${plural(s.targets.d30, 'target')} reported in the last 30 days, ${plural(s.detections.d30, 'report')} screened.`
        + unverified(s) },
      { icon: Send, title: 'Takedowns in motion', body:
        `${plural(s.actions.filed_30d, 'removal request')} filed in the last 30 days`
        + (s.actions.pending > 0 ? `, ${s.actions.pending} planned` : '')
        + `; ${plural(s.contained.d30, 'page')} reported this month ${s.contained.d30 === 1 ? 'is' : 'are'} already offline`
        + (s.contained.rate_90d != null ? ` (${percent(s.contained.rate_90d)} of the last 90 days' targets)` : '') + '.' },
      { icon: Users, title: 'Customers kept safe', body:
        `Every live target stays on the monitor's rolling re-probe; the last check ran ${lastCheck}. `
        + `${plural(s.channels.count, 'source')} keep reporting${s.channels.count > 0 ? ` (${sourcesList})` : ''}, so a new page is on this board as soon as one of them sees it.` },
    ],
  }
}

// ─── Copy from the two signals (the deck's shape) ────────────────────────────

type Signals = {
  headline: string[]
  items: Item[]
  feedLabel: string | null
}

function composeSignals(status: IntegrityStatus, s: Summary, brand: string): Signals {
  const pages = s.pages!, comms = s.communications!, feed = s.feed!, weeks = s.by_week ?? []
  const since = shortDate(s.window?.since)
  const hasFeed = Object.keys(feed.sources).length > 0
  const feedLabel = hasFeed ? Object.values(feed.sources)[0] : null
  const live = pages.live_in_window
  const detected = pages.detected_in_window

  const headline: string[] = []
  if (status === 'secure') headline.push(detected > 0 ? `No page up today, of ${detected} detected since ${since}.` : `No page impersonating ${brand} detected since ${since}.`)
  else headline.push(`${plural(live, 'page')} up today, of ${detected} detected since ${since}.`)
  if (hasFeed && detected > 0) headline.push(`${pages.in_feed_in_window === 0 ? 'None' : pages.in_feed_in_window} flagged by the ${feedLabel!.toLowerCase()}.`)
  if (comms.in_window > 0) headline.push(`${plural(comms.in_window, 'fraud message')} reaching consumers.`)

  // Pace: the window's first half against its second half.
  const half = Math.floor(weeks.length / 2)
  const first = weeks.slice(0, half), second = weeks.slice(half)
  const p1 = first.reduce((n, w) => n + w.pages, 0), p2 = second.reduce((n, w) => n + w.pages, 0)
  const items: Item[] = []
  if (detected > 0) {
    const rising = p2 > p1
    items.push({ icon: rising ? TrendingUp : Activity, title: rising ? 'Detections rising' : 'Detections steady', body:
      `${plural(p1, 'page')} impersonating ${brand} in the ${plural(first.length, 'week')} to ${shortDate(second[0]?.week_start)}, ${p2} in the ${plural(second.length, 'week')} since: `
      + `the pace went from ${rate(p1, first.length)} to ${rate(p2, second.length)} a week.` })
  }
  if (live > 0) {
    items.push({ icon: AlertCircle, title: 'No matching action', body:
      `${live} of the ${detected} pages detected since ${since} ${live === 1 ? 'is' : 'are'} up today`
      + (pages.oldest_live_days != null ? `, the oldest for ${plural(pages.oldest_live_days, 'day')}` : '') + '.'
      + (hasFeed ? ` ${pages.in_feed_in_window === 0 ? 'None of them appears' : `${pages.in_feed_in_window} of them ${pages.in_feed_in_window === 1 ? 'appears' : 'appear'}`} in a ${feedLabel!.toLowerCase()} send.` : '')
      + (s.actions.filed_window != null ? ` ${plural(s.actions.filed_window, 'removal request')} filed in the window${s.actions.pending > 0 ? `, ${s.actions.pending} planned` : ''}.` : '') })
  } else if (detected > 0) {
    items.push({ icon: ShieldCheck, title: 'Every detected page is down', body:
      `${pages.contained} of the ${detected} pages detected since ${since} were seen down; ${pages.stale + pages.unknown} not yet verified.`
      + (s.removal.timed_90d > 0 ? ` Median detection-to-removal of ${formatMinutes(s.removal.median_minutes_90d)} over the last 90 days.` : '') })
  }
  if (comms.in_window > 0) {
    const ch = comms.by_channel
    const parts = [ch.sms ? `${ch.sms} by SMS` : '', ch.email ? `${ch.email} by email` : '', ch.whatsapp ? `${ch.whatsapp} by WhatsApp` : '', ch.other ? `${ch.other} other` : ''].filter(Boolean)
    items.push({ icon: MessageSquare, title: 'Consumers keep receiving', body:
      `${plural(comms.in_window, 'fraud message')} in ${brand}'s name since ${shortDate(comms.first_in_window)}, ${parts.join(', ')}; `
      + `${comms.unrelated_to_pages} of them point at none of the detected pages: shortened links, legitimate services used as hosting, or no link at all.` })
  }
  if (s.removal.timed_90d > 0 && live > 0) {
    items.push({ icon: Send, title: 'Takedowns in motion', body:
      `${plural(s.actions.filed_30d, 'removal request')} filed in the last 30 days; ${plural(s.contained.d30, 'page')} reported this month already offline`
      + (s.contained.rate_90d != null ? ` (${percent(s.contained.rate_90d)} of the last 90 days' targets)` : '') + '.' })
  }
  return { headline, items, feedLabel }
}

// ─── Weekly bars: one SVG, values above the bars, week labels below ──────────

function WeeklyBars({ values, labels, hot, hotClass, title }: { values: number[]; labels: string[]; hot: boolean[]; hotClass: string; title: string }) {
  const H = 44, BW = 18, GAP = 8, mx = Math.max(1, ...values)
  const width = values.length * (BW + GAP) - GAP
  return (
    <svg viewBox={`-2 -12 ${width + 4} ${H + 34}`} width={width + 4} height={H + 34} role="img" aria-label={title} className="block max-w-full">
      {values.map((v, i) => {
        const h = Math.max(3, Math.round((v / mx) * H)), x = i * (BW + GAP)
        return (
          <g key={i} className={hot[i] ? hotClass : 'text-muted-foreground'}>
            <rect x={x} y={H - h + 4} width={BW} height={h} rx={3} fill="currentColor" opacity={hot[i] ? 1 : 0.35} />
            <text x={x + BW / 2} y={H - h - 1} textAnchor="middle" fontSize="9" fontWeight="700" fill="currentColor" opacity={hot[i] ? 1 : 0.7}>{v}</text>
            <text x={x + BW / 2} y={H + 20} textAnchor="middle" fontSize="7.5" className="fill-muted-foreground" fill="currentColor" opacity={0.7}>{labels[i]}</text>
          </g>
        )
      })}
    </svg>
  )
}

// ─── The orb (ported from the mock's CSS; --c is the status colour) ──────────

// While the summary loads the orb is already there, colourless (--c is a
// neutral grey) with a spinning arc for a border; when it lands, --c takes the
// status colour. --c is registered as a <color> so that change animates
// instead of snapping, and the arc gives way to the usual ripple.
const ORB_CSS = `
@property --c { syntax: '<color>'; inherits: true; initial-value: oklch(72% 0 0); }
.bi-stage { position: relative; display: grid; place-items: center; transition: --c 1.2s ease; }
.bi-stage.bi-pending { --c: oklch(72% 0 0); }
.bi-stage::after {
  content: ""; position: absolute; left: 50%; bottom: -9%; translate: -50%;
  width: 58%; height: 12%; border-radius: 9999px; filter: blur(11px);
  background: radial-gradient(ellipse at center, color-mix(in oklab, var(--c) 60%, transparent) 0%, transparent 70%);
}
.bi-orb {
  position: relative; border-radius: 9999px;
  background:
    radial-gradient(circle at 30% 18%, oklch(100% 0 0) 0%, oklch(100% 0 0 / .95) 1.5%, oklch(100% 0 0 / 0) 4.5%),
    radial-gradient(circle at 22% 30%, oklch(100% 0 0 / .9) 0%, oklch(100% 0 0 / 0) 2.5%),
    radial-gradient(circle at 78% 82%, oklch(100% 0 0 / .9) 0%, oklch(100% 0 0 / 0) 3%),
    radial-gradient(ellipse 40% 15% at 50% 91%, color-mix(in oklab, var(--c) 75%, white 25%) 0%, transparent 66%),
    radial-gradient(circle at 50% 50%, transparent 73%, color-mix(in oklab, var(--c) 62%, transparent) 90%, color-mix(in oklab, var(--c) 26%, transparent) 100%),
    linear-gradient(177deg, var(--g1) 0%, var(--g2) 48%, var(--g1) 100%),
    radial-gradient(circle at 50% 46%, var(--g3) 0%, var(--g4) 65%, var(--g5) 100%);
  box-shadow:
    0 0 24px 1px color-mix(in oklab, var(--c) 22%, transparent),
    0 32px 42px -30px oklch(30% .03 260 / .4),
    inset 0 0 0 1px oklch(100% 0 0 / .8),
    inset 0 10px 20px oklch(100% 0 0 / .6),
    inset 0 -12px 24px color-mix(in oklab, var(--c) 28%, transparent);
  transition: box-shadow .6s;
  animation: bi-breathe 5s ease-in-out infinite;
}
.bi-orb::before {
  content: ""; position: absolute; inset: 0; border-radius: 9999px; pointer-events: none;
  box-shadow: inset 0 0 9px 3px color-mix(in oklab, var(--c) 60%, oklch(30% .05 260 / .85)), inset 0 0 2px oklch(28% .04 260 / .45);
}
.bi-orb::after {
  content: ""; position: absolute; inset: -12%; border-radius: 9999px;
  border: 1px solid color-mix(in oklab, var(--c) 20%, transparent);
  animation: bi-ripple 4.5s ease-out infinite;
}
.bi-orb.bi-danger { animation-duration: 2.2s; }
.bi-orb.bi-danger::after { animation-duration: 2.2s; }
.bi-orb.bi-loading::after {
  inset: -7%; border: 2px solid color-mix(in oklab, var(--c) 18%, transparent);
  border-top-color: color-mix(in oklab, var(--c) 85%, transparent);
  animation: bi-spin 1.1s linear infinite;
}
.bi-light { --g1: oklch(90% .02 250 / .45); --g2: oklch(99% .005 95 / .2); --g3: oklch(99% .005 95 / .35); --g4: oklch(96% .01 240 / .3); --g5: oklch(92% .015 240 / .4); }
.bi-dark  { --g1: oklch(40% .03 250 / .55); --g2: oklch(60% .01 95 / .25); --g3: oklch(70% .01 95 / .35); --g4: oklch(45% .02 240 / .4); --g5: oklch(30% .02 240 / .5); }
@keyframes bi-breathe { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.03); } }
@keyframes bi-ripple { 0% { opacity: .6; transform: scale(.85); } 100% { opacity: 0; transform: scale(1.35); } }
@keyframes bi-spin { to { transform: rotate(360deg); } }
@media (prefers-reduced-motion: reduce) {
  .bi-orb, .bi-orb::after { animation: none; }
  /* the arc still turns while computing, slowly; nothing else moves */
  .bi-orb.bi-loading::after { animation: bi-spin 3s linear infinite; }
}
`

// ─── View ─────────────────────────────────────────────────────────────────────

export function BrandIntegrityView({ theme, platformId, brandLabel, brandId, isEditor }: {
  theme: ProcessedTheme
  platformId: number
  brandLabel: string
  brandId: string
  isEditor?: boolean
}) {
  const { isDark, border, muted, label, text } = baseDashboardClasses(theme)

  const cached = _cache.get(platformId)
  const fresh = cached != null && Date.now() - cached.at < CACHE_TTL
  const [summary, setSummary] = useState<Summary | null>(fresh ? cached.summary : null)
  const [lastUpdated, setLastUpdated] = useState<number | null>(fresh ? cached.at : null)
  const [loading, setLoading] = useState(!fresh)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // An Editor's preview of another status; null = the computed one.
  const [preview, setPreview] = useState<IntegrityStatus | null>(null)

  const load = useCallback(async (manual = false) => {
    if (manual) setRefreshing(true)
    setError(null)
    const result = await apiGet<Summary>(`/brand-integrity?platform_id=${platformId}`)
    if (result.success && result.data) {
      const at = Date.now()
      _cache.set(platformId, { summary: result.data, at })
      setSummary(result.data)
      setLastUpdated(at)
    } else {
      setError(result.error ?? 'Failed to load')
    }
    setLoading(false)
    setRefreshing(false)
  }, [platformId])

  useEffect(() => {
    setPreview(null)
    const c = _cache.get(platformId)
    if (c && Date.now() - c.at < CACHE_TTL) {
      setSummary(c.summary); setLastUpdated(c.at); setLoading(false)
      return
    }
    setSummary(null); setLoading(true)
    void load()
  }, [platformId, load])

  // No summary yet (first load, or a brand switch): the orb waits colourless.
  const pending = loading || !summary
  const computed = summary?.status ?? null
  const status: IntegrityStatus = preview ?? computed ?? 'secure'
  const hasSignals = !!summary?.pages && !!summary?.communications && !!summary?.feed && !!summary?.by_week
  const legacy = useMemo(() => (summary && !hasSignals ? composeLegacyCopy(status, summary, brandLabel) : null), [status, summary, hasSignals, brandLabel])
  const signals = useMemo(() => (summary && hasSignals ? composeSignals(status, summary, brandLabel) : null), [status, summary, hasSignals, brandLabel])
  const tone = isDark ? STATUS_TEXT[status].dark : STATUS_TEXT[status].light
  const heading = 'text-foreground'
  const body = 'text-muted-foreground'
  const hairline = 'border-border'

  const info = summary ? (
    <div className="space-y-1.5 text-[12px]">
      <p>Computed on read from this brand&apos;s unified entries (platform id {summary.platform_id}), the auto-domain candidates judged a brand lure, their probes and the actions filed for it. A <b>page</b> is a site impersonating the brand; it is <b>live</b> when its newest probe saw it up, the page is not a parked domain, a provider block or a hosting placeholder, and nobody marked it inactive. A <b>fraud message</b> is a scam report that is an SMS, email or WhatsApp message to a consumer.</p>
      <p><b>Under threat</b>: at least {summary.rule.underThreatLiveTargets} live pages, or one live for {summary.rule.underThreatLiveAgeHours}h+ since detection. <b>Elevated</b>: any live page, or a scam report in the last {summary.rule.elevatedRecentDays} days. Otherwise <b>secure</b>.</p>
      <p>A page last seen up but not re-probed for {summary.rule.staleProbeDays}+ days is unverified, and one never probed is unknown: neither counts as live or contained.{summary.window ? ` The window is the last ${summary.window.weeks} weeks (since ${shortDate(summary.window.since)}); the status looks at everything.` : ''}</p>
    </div>
  ) : undefined

  const statusControl = (
          <div className="flex items-center gap-2">
            {preview && preview !== computed && (
              <span className={`font-mono text-[9px] uppercase tracking-widest ${muted}`}>preview · computed is {computed ? STATUS_LABEL[computed] : '—'}</span>
            )}
            <div className={`flex gap-1 rounded border p-1 ${border} bg-card`} role="radiogroup" aria-label="Status">
              {STATUS_ORDER.map((st) => {
                // Nothing is highlighted until the status is known.
                const active = !!summary && st === status
                const canPreview = !!isEditor && !!summary
                return (
                  <button
                    key={st}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    disabled={!canPreview}
                    title={canPreview ? (st === computed ? 'Computed status' : `Preview the page as ${STATUS_LABEL[st]}`) : undefined}
                    onClick={() => setPreview(st === computed ? null : st)}
                    className={`relative rounded px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-widest transition ${
                      active
                        ? ('bg-primary text-primary-foreground')
                        : `${muted} ${canPreview ? 'hover:text-signal' : 'cursor-default'}`
                    }`}
                  >
                    {STATUS_LABEL[st]}
                    {st === computed && !active && <span className="absolute right-1 top-1 h-1 w-1 rounded-full bg-signal" aria-hidden />}
                  </button>
                )
              })}
            </div>
          </div>
  )

  const weeks = summary?.by_week ?? []
  const weekLabels = weeks.map((w) => shortDate(w.week_start))
  const hotPages = weeks.map((w, i) => i >= weeks.length - 3)
  const hotComms = weeks.map((w, i) => i >= weeks.length - 3)

  const tile = (labelText: string, value: React.ReactNode, qualifier?: string, valueClass = heading, sup?: string) => (
    <div className="min-w-0 px-4 py-3 first:pl-0">
      <p className={`font-mono text-[9px] uppercase tracking-[0.2em] ${muted}`}>{labelText}{sup && <sup className="ml-0.5 text-signal">{sup}</sup>}</p>
      <p className={`mt-1 flex items-baseline gap-2 font-mono text-3xl tabular-nums ${valueClass}`}>
        {value}
        {qualifier && <span className={`text-[12px] font-sans ${muted}`}>{qualifier}</span>}
      </p>
    </div>
  )

  const storyItems = (items: Item[]) => (
    <div className={`border-t ${hairline}`}>
      {items.map((item, i) => {
        const Icon = item.icon
        return (
          <div key={item.title} className={`grid grid-cols-[2.5rem_1fr] gap-x-2 py-5 ${i > 0 ? `border-t ${hairline}` : ''}`}>
            <span className={`pt-1 font-mono text-[10px] ${muted}`}>{String(i + 1).padStart(2, '0')}</span>
            <div>
              <div className={`flex items-center gap-2 text-[15px] font-semibold ${text}`}>
                <Icon className={`h-4 w-4 ${label}`} />
                {item.title}
              </div>
              <p className={`mt-1.5 max-w-2xl text-[13px] leading-relaxed ${body}`}>{item.body}</p>
            </div>
          </div>
        )
      })}
    </div>
  )

  return (
    <PageFrame
      eyebrow={`${brandLabel} · Moderation`}
      title="Brand integrity"
      info={info}
      refresh={{ onRefresh: () => { _cache.delete(platformId); void load(true) }, refreshing, lastUpdated, storageKey: `${brandId}-integrity` }}
      headerActions={statusControl}
    >
      <style>{ORB_CSS}</style>
      <div className="mx-auto w-full max-w-5xl px-4 pb-16 pt-6 md:px-8">

        {error && !loading && (
          <div className="flex h-64 flex-col items-center justify-center gap-3">
            <span className="font-mono text-[13px] text-destructive">{error}</span>
            <button onClick={() => void load(true)} className={`border px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest hover:border-signal hover:text-signal ${border} ${muted}`}>Retry</button>
          </div>
        )}

        {(loading || summary) && (
          <>
            {/* ── Legacy backend: three numbers above the orb ───────────── */}
            {(pending || legacy) && (
              <div className={`mt-8 grid grid-cols-3 divide-x border-y ${hairline} divide-border`}>
                {pending || !legacy
                  ? [0, 1, 2].map((i) => (
                      <div key={i} className="px-5 py-5 first:pl-0" aria-hidden>
                        <div className="h-2 w-24 rounded-sm bg-muted" />
                        <div className="mt-3 h-7 w-12 animate-pulse rounded-sm bg-muted" />
                      </div>
                    ))
                  : legacy.tiles.map((t) => (
                      <div key={t.label} className="px-5 py-5 first:pl-0">
                        <p className={`font-mono text-[9px] uppercase tracking-[0.2em] ${muted}`}>{t.label}</p>
                        <p className={`mt-1 font-mono text-3xl tabular-nums ${tone}`}>{t.value}</p>
                      </div>
                    ))}
              </div>
            )}

            {/* ── The orb: colourless with a spinning border while computing,
                 the status colour once the summary is in ─────────────────── */}
            <div className="mt-10 flex flex-col items-center">
              <div
                className={`bi-stage bi-light ${pending ? 'bi-pending' : ''}`}
                style={pending ? undefined : { ['--c' as string]: STATUS_COLOR[status] }}
              >
                <div
                  className={`bi-orb h-48 w-48 md:h-60 md:w-60 ${!pending && status === 'under_threat' ? 'bi-danger' : ''} ${pending || refreshing ? 'bi-loading' : ''}`}
                  role={pending ? 'progressbar' : undefined}
                  aria-label={pending ? 'Computing the status' : `Status: ${STATUS_LABEL[status]}`}
                />
              </div>
              <p className={`mt-10 font-mono text-[10px] uppercase tracking-[0.3em] ${pending ? muted : tone}`}>{pending ? 'Computing…' : STATUS_LABEL[status]}</p>
              {signals
                ? <h2 className={`mt-2 text-center text-xl font-semibold leading-snug tracking-tight md:text-2xl ${heading}`}>
                    {signals.headline.map((line, i) => <span key={i} className="block">{line}</span>)}
                  </h2>
                : <h2 className={`mt-2 text-center text-2xl font-semibold tracking-tight md:text-3xl ${heading} ${pending ? 'invisible' : ''}`}>{STATUS_HEADLINE[status]}</h2>}
            </div>

            {/* ── Trust Fence detection: the two signals, then the story ─── */}
            {!pending && summary && signals && (
              <>
                <div className="mt-12 rounded-2xl border border-signal/40 px-6 pb-2 pt-5">
                  <div className="flex items-center gap-2.5">
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img src="/logo.png" alt="" className="h-6 w-6 object-contain" />
                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-signal">Trust Fence detection</span>
                  </div>

                  {summary.communications!.in_window > 0 && (
                    <div className="mt-4 grid grid-cols-[1fr_1fr_1.5fr] divide-x divide-border rounded-xl border border-success/60 px-4">
                      {tile('Fraud messages', <span className="text-success">{summary.communications!.in_window}</span>,
                        [summary.communications!.by_channel.sms ? `${summary.communications!.by_channel.sms} SMS` : '', summary.communications!.by_channel.email ? `${summary.communications!.by_channel.email} email` : '', summary.communications!.by_channel.whatsapp ? `${summary.communications!.by_channel.whatsapp} WhatsApp` : ''].filter(Boolean).join(' · '))}
                      {tile('Unrelated to detected pages', summary.communications!.unrelated_to_pages, `of ${summary.communications!.in_window}`)}
                      <div className="min-w-0 px-4 py-3">
                        <p className={`font-mono text-[9px] uppercase tracking-[0.2em] ${muted}`}>Messages per week</p>
                        <div className="mt-1"><WeeklyBars values={weeks.map((w) => w.communications)} labels={weekLabels} hot={hotComms} hotClass="text-success" title="Fraud messages per week" /></div>
                      </div>
                    </div>
                  )}

                  <div className={`mt-3 grid grid-cols-[1fr_1fr_1.5fr] divide-x divide-border border-y ${hairline} px-4`}>
                    {tile('Live pages', <span className={summary.pages!.live_in_window > 0 ? 'text-destructive' : 'text-success'}>{summary.pages!.live_in_window}</span>, `of ${summary.pages!.detected_in_window} detected`)}
                    {signals.feedLabel
                      ? tile(`Flagged by the ${signals.feedLabel.toLowerCase()}`, summary.pages!.in_feed_in_window, `of ${summary.pages!.detected_in_window}`)
                      : tile('Pages down', summary.pages!.contained, `of ${summary.pages!.detected} ever`)}
                    <div className="min-w-0 px-4 py-3">
                      <p className={`font-mono text-[9px] uppercase tracking-[0.2em] ${muted}`}>Detections per week</p>
                      <div className="mt-1"><WeeklyBars values={weeks.map((w) => w.pages)} labels={weekLabels} hot={hotPages} hotClass="text-destructive" title="Pages detected per week" /></div>
                    </div>
                  </div>

                  <div className="mt-2">{storyItems(signals.items)}</div>
                </div>

                {/* ── The third-party feed, below ours ───────────────────── */}
                {signals.feedLabel && (
                  <div className={`mt-4 rounded-xl border px-6 py-4 ${hairline}`}>
                    <p className={`font-mono text-[9px] uppercase tracking-[0.2em] ${muted}`}>{signals.feedLabel} · same window</p>
                    <div className="mt-2 flex flex-wrap gap-x-10 gap-y-2">
                      <p className={`flex items-baseline gap-2 ${body}`}><span className={`font-mono text-2xl tabular-nums ${heading}`}>{summary.feed!.domains_in_window}</span><span className="text-[13px]">domains reported since {shortDate(summary.window?.since)}</span></p>
                      <p className={`flex items-baseline gap-2 ${body}`}><span className={`font-mono text-2xl tabular-nums ${heading}`}>{summary.feed!.pre_flagged_in_window}</span><span className="text-[13px]">already on the Trust Fence monitoring list before the send</span></p>
                      <p className={`flex items-baseline gap-2 ${body}`}><span className={`font-mono text-2xl tabular-nums ${summary.feed!.live > 0 ? 'text-destructive' : heading}`}>{summary.feed!.live}</span><span className="text-[13px]">still up today</span></p>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* ── Legacy story ───────────────────────────────────────────── */}
            {!pending && summary && legacy && <div className="mt-12">{storyItems(legacy.items)}</div>}

            {!pending && summary && (<>
            {/* ── Live targets, when there are any ──────────────────────── */}
            {summary.live.count > 0 && (
              <div className={`mt-10 border-t pt-6 ${hairline}`}>
                <p className={`font-mono text-[9px] uppercase tracking-[0.2em] ${muted}`}>Live pages · {summary.live.count}</p>
                <ul className={`mt-3 divide-y divide-border`}>
                  {summary.live.targets.map((t) => (
                    <li key={t.host} className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 py-2.5">
                      <span className={`truncate font-mono text-[13px] ${text}`} title={t.url}>{t.host}{t.feed && signals?.feedLabel ? <span className={`ml-2 font-mono text-[9px] uppercase tracking-widest ${muted}`}>{signals.feedLabel}</span> : null}</span>
                      <span className={`flex items-center gap-4 font-mono text-[10px] uppercase tracking-widest ${muted}`}>
                        {t.reports > 0 && <span className="flex items-center gap-1"><Eye className="h-3 w-3" />{plural(t.reports, 'report')}</span>}
                        <span className="flex items-center gap-1"><Clock className="h-3 w-3" />seen {relativeTime(t.detected_at)}</span>
                        <span>checked {relativeTime(t.last_checked_at)}</span>
                      </span>
                    </li>
                  ))}
                  {summary.live.count > summary.live.targets.length && (
                    <li className={`py-2 font-mono text-[10px] uppercase tracking-widest ${muted}`}>and {summary.live.count - summary.live.targets.length} more</li>
                  )}
                </ul>
              </div>
            )}

            <p className={`mt-10 text-center font-mono text-[9px] uppercase tracking-[0.25em] ${muted}`}>
              Continuously monitored across {plural(summary.channels.count, 'channel')} · computed {relativeTime(summary.computed_at)}
            </p>
            </>)}
          </>
        )}
      </div>
    </PageFrame>
  )
}
